import React, { useState, useEffect } from 'react';
import { Product } from '../../types/productIntegration';

interface ProductFormProps {
  product?: Product; // For editing existing product
  onSave: (product: Omit<Product, 'id'> | Product) => void;
  onCancel: () => void;
  isOpen: boolean;
}

const ProductForm: React.FC<ProductFormProps> = ({ product, onSave, onCancel, isOpen }) => {
  const [formData, setFormData] = useState<Omit<Product, 'id'>>({
    name: '',
    category: '',
    team: '',
    type: 'internal',
    status: 'active',
    productContact: '',
    engineeringContact: '',
    description: '',
    version: '',
    icon: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [iconPreview, setIconPreview] = useState<string | null>(null);

  useEffect(() => {
    if (product) {
      // Editing existing product
      setFormData({
        name: product.name,
        category: product.category,
        team: product.team,
        type: product.type,
        status: product.status,
        productContact: product.productContact || '',
        engineeringContact: product.engineeringContact || '',
        description: product.description || '',
        version: product.version || '',
        icon: product.icon || ''
      });
      // Set icon preview for existing product
      if (product.icon) {
        setIconPreview(`/icons/${product.icon}`);
      }
    } else {
      // Reset form for new product
      setFormData({
        name: '',
        category: '',
        team: '',
        type: 'internal',
        status: 'active',
        productContact: '',
        engineeringContact: '',
        description: '',
        version: '',
        icon: ''
      });
      setIconPreview(null);
    }
    setErrors({});
    setSelectedFile(null);
  }, [product, isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle file upload
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({
          ...prev,
          icon: 'Please select a valid image file (PNG, JPG, GIF, or SVG)'
        }));
        return;
      }

      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        setErrors(prev => ({
          ...prev,
          icon: 'File size must be less than 2MB'
        }));
        return;
      }

      setSelectedFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (event) => {
        setIconPreview(event.target?.result as string);
      };
      reader.readAsDataURL(file);

      // Generate filename from product name or use original filename
      const fileName = file.name;
      setFormData(prev => ({
        ...prev,
        icon: fileName
      }));

      // Clear any previous errors
      setErrors(prev => ({
        ...prev,
        icon: ''
      }));
    }
  };

  // Upload file to server/public directory
  const uploadFile = async (file: File, fileName: string): Promise<boolean> => {
    try {
      const formData = new FormData();
      formData.append('icon', file);
      formData.append('fileName', fileName);

      const response = await fetch('/api/upload/icon', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to upload file');
      }

      return true;
    } catch (error) {
      console.error('Error uploading file:', error);
      setErrors(prev => ({
        ...prev,
        icon: 'Failed to upload icon. Please try again.'
      }));
      return false;
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Product name is required';
    }
    if (!formData.category.trim()) {
      newErrors.category = 'Category is required';
    }
    if (!formData.team.trim()) {
      newErrors.team = 'Team is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      // If there's a new file to upload, upload it first
      if (selectedFile && formData.icon) {
        const uploadSuccess = await uploadFile(selectedFile, formData.icon);
        if (!uploadSuccess) {
          return; // Don't proceed if upload failed
        }
      }

      if (product) {
        // Editing existing product
        onSave({ ...formData, id: product.id } as Product);
      } else {
        // Creating new product
        onSave(formData);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content product-form-modal">
        <div className="modal-header">
          <h3>{product ? 'Edit Product' : 'Create New Product'}</h3>
          <button className="close-modal" onClick={onCancel}>×</button>
        </div>
        
        <form onSubmit={handleSubmit} className="product-form">
          <div className="form-group">
            <label htmlFor="name">Product Name *</label>
            <input
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleInputChange}
              className={errors.name ? 'error' : ''}
              placeholder="Enter product name"
            />
            {errors.name && <span className="error-message">{errors.name}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="category">Category *</label>
            <input
              id="category"
              name="category"
              type="text"
              value={formData.category}
              onChange={handleInputChange}
              className={errors.category ? 'error' : ''}
              placeholder="e.g., Incident Management, Communication"
            />
            {errors.category && <span className="error-message">{errors.category}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="team">Team *</label>
            <input
              id="team"
              name="team"
              type="text"
              value={formData.team}
              onChange={handleInputChange}
              className={errors.team ? 'error' : ''}
              placeholder="e.g., Emergency Call Management"
            />
            {errors.team && <span className="error-message">{errors.team}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="type">Type</label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleInputChange}
              >
                <option value="internal">Internal (MSI)</option>
                <option value="external">External</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
              >
                <option value="active">Active</option>
                <option value="development">Development</option>
                <option value="deprecated">Deprecated</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="productContact">Product Contact</label>
              <input
                id="productContact"
                name="productContact"
                type="text"
                value={formData.productContact}
                onChange={handleInputChange}
                placeholder="Product owner name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="engineeringContact">Engineering Contact</label>
              <input
                id="engineeringContact"
                name="engineeringContact"
                type="text"
                value={formData.engineeringContact}
                onChange={handleInputChange}
                placeholder="Technical lead name"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="version">Version</label>
            <input
              id="version"
              name="version"
              type="text"
              value={formData.version}
              onChange={handleInputChange}
              placeholder="e.g., v1.0.0"
            />
          </div>

          <div className="form-group">
            <label htmlFor="icon">Product Icon</label>
            <div className="file-upload-container">
              <input
                id="icon"
                name="icon"
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/gif,image/svg+xml"
                onChange={handleFileChange}
                className="file-input"
              />
              <label htmlFor="icon" className="file-input-label">
                {selectedFile ? selectedFile.name : 'Choose icon file...'}
              </label>
              {iconPreview && (
                <div className="icon-preview">
                  <img src={iconPreview} alt="Icon preview" className="preview-image" />
                </div>
              )}
            </div>
            {errors.icon && <span className="error-message">{errors.icon}</span>}
            <small className="field-help">
              Upload an icon image (PNG, JPG, GIF, or SVG). Max size: 2MB. Recommended size: 64x64 pixels.
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Brief description of the product"
              rows={3}
            />
          </div>

          <div className="form-actions">
            <button type="button" onClick={onCancel} className="cancel-btn">
              Cancel
            </button>
            <button type="submit" className="save-btn">
              {product ? 'Update Product' : 'Create Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductForm;
